import React, { useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    Box,
    Collapse,
    IconButton
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function StatusCard({ competitor }) {
    const [open, setOpen] = useState(false);
    const handleToggle = () => setOpen(!open);

    const fullName = `${competitor.firstName ?? ""} ${competitor.lastName ?? ""}`.trim();

    // Bord gauche visible uniquement si temps disponible
    const borderColor = competitor.timeSec ? "#f1242e" : "transparent";

    // Inters dynamiques (inter1, inter2, ...) — uniquement s'ils sont présents
    // et non vides (télémark avec DISCIPLINE_TYPE=20 par ex).
    const interEntries = Object.keys(competitor)
        .filter((k) => /^inter\d+$/i.test(k) && competitor[k])
        .sort((a, b) => Number(a.replace(/\D/g, "")) - Number(b.replace(/\D/g, "")));

    const hasPenalties = Boolean(competitor.penGate || competitor.penJump);
    const hasExtraDetails = interEntries.length > 0 || hasPenalties;

    return (
        <Card
            sx={{
                mb: 1,
                borderLeft: `6px solid ${borderColor}`,
                backgroundColor: "#ffffff",
                color: "#000000",
                "&:hover": {
                    borderLeftColor: "#f1242e",
                    backgroundColor: "#f5f5f5",
                },
            }}
        >
            <CardContent
                sx={{
                    display: "flex",
                    alignItems: "center",
                    p: 1,
                    cursor: "pointer",
                }}
                onClick={handleToggle}
            >
                {/* Ordre */}
                <Box sx={{ minWidth: 32, textAlign: "center" }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {competitor.line ?? competitor.rank ?? ""}
                    </Typography>
                </Box>

                {/* Ligne principale */}
                <Box
                    sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    {/* Nom + bib — prend toute la largeur disponible (1fr),
                        tronqué avec "…" seulement si ça ne rentre vraiment pas */}
                    <Typography
                        variant="body1"
                        sx={{
                            minWidth: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                    >
                        #{competitor.bib} {fullName}
                    </Typography>

                    {/* Temps aligné à droite */}
                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                            {competitor.timeSec || ""}
                        </Typography>

                        {/* Différence de temps */}
                        {competitor.diff && (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontStyle: "italic",
                                    color: "#022a5b",
                                    fontSize: "0.8rem",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {competitor.diff}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Flèche (affichée seulement si temps ou détails) */}
                {(competitor.timeSec || competitor.nation || competitor.club || hasExtraDetails) && (
                    <IconButton
                        sx={{
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.3s ease",
                        }}
                    >
                        <ExpandMoreIcon />
                    </IconButton>
                )}
            </CardContent>

            {/* Détails */}
            <Collapse in={open} timeout="auto" unmountOnExit>
                <CardContent sx={{ pt: 0 }}>
                    {/* Inters (télémark) — juste sous le temps/diff affichés au-dessus */}
                    {interEntries.map((key) => (
                        <Typography variant="body2" key={key}>
                            Inter {key.replace(/\D/g, "")}: {competitor[key]}
                        </Typography>
                    ))}

                    {/* Pénalités (télémark) */}
                    {competitor.penGate && (
                        <Typography variant="body2">
                            Pén. gates: {competitor.penGate}
                        </Typography>
                    )}
                    {competitor.penJump && (
                        <Typography variant="body2">
                            Pén. jump: {competitor.penJump}
                        </Typography>
                    )}

                    <Typography variant="body2">
                        Nation: {competitor.nation || "—"}
                    </Typography>
                    <Typography variant="body2">
                        Club: {competitor.club || "—"}
                    </Typography>
                    <Typography variant="body2">
                        Sexe: {competitor.sex || "—"}
                    </Typography>
                    <Typography variant="body2">
                        Année: {competitor.yearOfBirth || "—"}
                    </Typography>

                    {/* Statut tout en bas — masqué si le résultat est OK */}
                    {competitor.status && competitor.status !== "OK" && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                            Statut: {competitor.status}
                        </Typography>
                    )}
                </CardContent>
            </Collapse>
        </Card>
    );
}